import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, b2bApi, errMsg } from "@/lib/api";
import { useT } from "@/context/I18nContext";
import PageHeader from "@/components/common/PageHeader";
import { Stat, StatusPill, Card, fmtDate, fmtDateTime, asList } from "@/components/common/Stat";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CreditCard, Layers, Building2 } from "lucide-react";

export function OwnerBilling() {
  const t = useT();
  const q = useQuery({ queryKey: ["owner-b2b-billing"], queryFn: async () => (await b2bApi.get("/b2b/billing/admin/overview")).data, retry: false });
  const totals = q.data?.totals || {};
  return (
    <div className="space-y-6">
      <PageHeader eyebrow={t("b2b.nav.owner")} title={t("b2b.owner.billingTitle")} subtitle={t("b2b.owner.billingSub")} />
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <Stat icon={Building2} label={t("b2b.owner.orgs")} value={totals.organisations ?? 0} testId="owner-billing-stat-orgs" />
        <Stat icon={CreditCard} label={t("b2b.owner.activeLicences")} value={totals.active ?? 0} testId="owner-billing-stat-active" />
        <Stat icon={Layers} label={t("b2b.owner.seats")} value={totals.seats_purchased ?? 0} testId="owner-billing-stat-seats" />
      </div>
      <div className="rounded-3xl bg-white shadow-card overflow-hidden" data-testid="owner-billing-table">
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-[0.15em] text-ink-500 text-left">
            <tr className="border-b border-border"><th className="px-5 py-3">Org</th><th className="px-5 py-3">{t("b2b.billing.status")}</th><th className="px-5 py-3">Plan</th><th className="px-5 py-3">{t("b2b.billing.seats")}</th><th className="px-5 py-3">{t("b2b.billing.renews")}</th><th className="px-5 py-3">Stripe</th></tr>
          </thead>
          <tbody className="divide-y divide-border">
            {(q.data?.licenses || []).map((l) => (
              <tr key={l.org_id} data-testid={`owner-billing-row-${l.org_id}`}>
                <td className="px-5 py-3 font-medium"><Link to={`/owner/organizations/${l.org_id}`} className="text-ink-900 hover:underline">{l.org_id}</Link></td>
                <td className="px-5 py-3"><StatusPill status={l.status} label={t(`b2b.billing.states.${l.status}`)} />{l.cancel_at_period_end && <span className="ml-2 text-xs text-ink-500">{t("b2b.billing.cancelsAt")}</span>}</td>
                <td className="px-5 py-3 text-ink-700">{l.plan_id || "—"} · {l.interval || "—"}</td>
                <td className="px-5 py-3 text-ink-700">{l.seats_purchased}</td>
                <td className="px-5 py-3 text-ink-500">{fmtDate(l.current_period_end)}</td>
                <td className="px-5 py-3 font-mono text-xs text-ink-500">{l.stripe_subscription_id || "—"}</td>
              </tr>
            ))}
            {!(q.data?.licenses || []).length && <tr><td colSpan={6} className="px-5 py-6 text-ink-500">{q.isLoading ? t("common.loading") : t("common.empty")}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function OwnerRoles() {
  const t = useT();
  const qc = useQueryClient();
  const grants = useQuery({ queryKey: ["owner-grants"], queryFn: async () => (await b2bApi.get("/b2b/admin/role-grants")).data, retry: false });
  const links = useQuery({ queryKey: ["owner-oh-links"], queryFn: async () => (await b2bApi.get("/b2b/admin/oh-links")).data, retry: false });
  const [email, setEmail] = useState(""); const [orgId, setOrgId] = useState(""); const [busy, setBusy] = useState(false);
  const grant = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await b2bApi.post("/b2b/admin/role-grants", { email: email.trim(), role: "occupational_health", org_id: orgId.trim() || null }); toast.success(t("common.saved")); setEmail(""); setOrgId(""); qc.invalidateQueries({ queryKey: ["owner-grants"] }); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const revoke = async (id) => {
    try { await b2bApi.delete(`/b2b/admin/role-grants/${id}`); toast.success(t("common.saved")); qc.invalidateQueries({ queryKey: ["owner-grants"] }); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const active = (grants.data?.grants || []).filter((g) => !g.revoked_at);
  return (
    <div className="space-y-6">
      <PageHeader eyebrow={t("b2b.nav.owner")} title={t("b2b.owner.rolesTitle")} subtitle={t("b2b.owner.rolesSub")} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title={t("b2b.owner.grantOh")} testId="owner-grant-form-card">
          <form onSubmit={grant} className="space-y-3" data-testid="owner-grant-form">
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("b2b.owner.grantee")} className="h-11 rounded-xl bg-white" data-testid="owner-grant-email" />
            <Input value={orgId} onChange={(e) => setOrgId(e.target.value)} placeholder="org_id (optional)" className="h-11 rounded-xl bg-white font-mono text-xs" data-testid="owner-grant-org" />
            <Button type="submit" disabled={busy} className="rounded-full bg-sage-500 hover:bg-sage-600 text-white h-11 w-full" data-testid="owner-grant-submit">{t("b2b.owner.grant")}</Button>
          </form>
        </Card>
        <Card title={t("b2b.nav.roles")} testId="owner-grants-list" className="lg:col-span-2">
          <div className="divide-y divide-border">
            {active.map((g) => (
              <div key={g.id} className="py-2.5 flex items-center justify-between gap-3 text-sm" data-testid={`owner-grant-${g.id}`}>
                <div className="min-w-0"><div className="text-ink-900 font-medium truncate">{g.email}</div><div className="text-xs text-ink-500">{g.role}{g.org_id ? ` · ${g.org_id}` : ""} · {fmtDate(g.created_at)} · by {g.granted_by}</div></div>
                <Button variant="outline" size="sm" className="rounded-full" onClick={() => revoke(g.id)} data-testid={`owner-grant-revoke-${g.id}`}>{t("b2b.owner.revoke")}</Button>
              </div>
            ))}
            {!active.length && <div className="text-sm text-ink-500 py-3">{grants.isLoading ? t("common.loading") : t("common.empty")}</div>}
          </div>
        </Card>
      </div>
      <Card title={t("b2b.owner.ohLinks")} testId="owner-oh-links">
        <p className="text-xs text-ink-500 mb-3">Oversight only — relationship and status. Shared categories and content are never visible here.</p>
        <div className="divide-y divide-border">
          {(links.data?.links || []).map((l) => (
            <div key={l.id} className="py-2 text-sm flex items-center justify-between gap-3"><span className="text-ink-900 truncate">{l.professional_email} → {l.employee_email}</span><StatusPill status={l.status} label={t(`b2b.oh.statuses.${l.status}`)} /></div>
          ))}
          {!(links.data?.links || []).length && <div className="text-sm text-ink-500 py-3">{t("common.empty")}</div>}
        </div>
      </Card>
    </div>
  );
}

export function OwnerAudit() {
  const t = useT();
  const platform = useQuery({ queryKey: ["owner-platform-audit"], queryFn: async () => (await api.get("/platform/audit")).data, retry: false });
  const b2b = useQuery({ queryKey: ["owner-b2b-audit"], queryFn: async () => (await b2bApi.get("/b2b/admin/audit", { params: { limit: 200 } })).data, retry: false });
  const plist = asList(platform.data, "items", "events", "audit");
  return (
    <div className="space-y-6">
      <PageHeader eyebrow={t("b2b.nav.owner")} title={t("b2b.owner.auditTitle")} subtitle={t("b2b.owner.auditSub")} />
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card title={t("b2b.owner.b2bAudit")} testId="owner-audit-b2b">
          <div className="divide-y divide-border max-h-[32rem] overflow-auto">
            {(b2b.data?.items || []).map((a) => (
              <div key={a.id} className="py-2.5 text-sm" data-testid={`audit-row-${a.id}`}>
                <div className="flex justify-between gap-3"><span className="font-mono text-xs text-ink-900">{a.action}</span><span className="text-xs text-ink-500 whitespace-nowrap">{fmtDateTime(a.created_at)}</span></div>
                <div className="text-xs text-ink-500 truncate">{a.actor_email} · {a.target || "—"}{a.org_id ? ` · ${a.org_id}` : ""}{a.meta && Object.keys(a.meta).length ? ` · ${JSON.stringify(a.meta)}` : ""}</div>
              </div>
            ))}
            {!(b2b.data?.items || []).length && <div className="text-sm text-ink-500 py-3">{b2b.isLoading ? t("common.loading") : t("common.empty")}</div>}
          </div>
        </Card>
        <Card title={t("b2b.owner.platformAudit")} testId="owner-audit-platform">
          <div className="divide-y divide-border max-h-[32rem] overflow-auto">
            {plist.map((a, i) => (
              <div key={a.id || i} className="py-2.5 text-sm">
                <div className="flex justify-between gap-3"><span className="font-mono text-xs text-ink-900">{a.action || a.event || a.type}</span><span className="text-xs text-ink-500 whitespace-nowrap">{fmtDateTime(a.created_at || a.ts || a.timestamp)}</span></div>
                <div className="text-xs text-ink-500 truncate">{a.actor_email || a.actor || a.user_email || "—"} · {a.target || a.target_email || a.subject || "—"}</div>
              </div>
            ))}
            {!plist.length && <div className="text-sm text-ink-500 py-3">{platform.isLoading ? t("common.loading") : t("common.empty")}</div>}
          </div>
        </Card>
      </div>
    </div>
  );
}
